import * as XLSX from 'xlsx';
import {
  platformFeatures,
  POST_TYPE_KEY,
  POST_TYPE_NAME_TO_TYPE_KEY,
  POST_TYPE_POST,
  VIDEO_MAX_ASPECT_RATIO,
  VIDEO_MIN_ASPECT_RATIO
} from '../../../components/lomavisCreator/config/platformFeatures';
import { textIsUrl } from '../../../components/lomavisCreator/config/buttonsValidatorsFunctions';
import {
  DRAFT,
  LABEL_TO_ACTIONS_MAP
} from '../../../components/lomavisCreator/config/constants';
import { transferDataToApiFormat } from '../lomavisCreator/lomavisCreator';
import {
  ALL,
  PLATFORM_LIST,
  PLATFORM_LIST_WITH_ALL,
  UPLOAD_TYPE_POST_MEDIA
} from '../../../config/constants';
import { GOOGLE_CTA_TYPE_CHOICES } from '../../../components/lomavisCreator/components/contants';
import axiosClient from '../../../utils/axios';
import { resolve } from '../../../api/endPoints';
import { generateFirebaseUploadUrl } from '../lomavisCreator/utils';
import axios from 'axios';
import { VIDEO } from '../../../components/communication/constants';

enum ImportError {
  INVALID_COLUMNS = 'invalid_format_columns',
  TOO_MANY_ENTRIES = 'invalid_format_entries_max_exceeded',
  DATE_NOT_FUTURE = 'invalid_format_date_not_in_future',
  TOO_MANY_PER_DAY = 'invalid_format_entries_per_day_exceeded',
  MISSING_DATE = 'no_date_provided',
  NO_PLATFORMS = 'invalid_format_no_platforms',
  INVALID_PLATFORM = 'invalid_format_platforms',
  NO_TEXT_OR_MEDIA = 'no_text_or_media_provided',
  MEDIA_COUNT_INVALID = 'invalid_format_media_count',
  MEDIA_COUNT_TOO_FEW = 'invalid_format_media_count_min_files',
  INVALID_MEDIA_URL = 'invalid_format_media_url',
  INVALID_POST_CREATE_ACTION = 'invalid_format_post_create_action',
  INVALID_POST_CREATE_ACTION_MULTIPOST_CREATOR = 'invalid_format_post_create_action_multipost_creator',
  INVALID_GOOGLE_CTA = 'invalid_format_google_cta',
  MEDIA_TYPE_NOT_SUPPORTED = 'invalid_format_media_type_not_supported',
  MULTITYPE_MEDIA_NOT_SUPPORTED = 'invalid_format_multitype_media_not_supported',
  MEDIA_SIZE_TOO_LARGE = 'invalid_format_media_size_too_large',
  MEDIA_ASPECT_RATIO_TOO_LARGE = 'invalid_format_media_aspect_ratio_too_large',
  MEDIA_ASPECT_RATIO_TOO_SMALL = 'invalid_format_media_aspect_ratio_too_small',
  INVALID_DATE_FORMAT = 'invalid_format_date',
  MEDIA_UPLOAD_FAILED = 'media_upload_failed',
  MEDIA_COUNT_TOO_LARGE = 'invalid_format_media_count_max_files',
  INVALID_POST_TYPE = 'invalid_format_post_type'
}

const NUMBER_OF_COLUMNS = 10;
const MAX_ENTRIES = 200;
const MAX_PER_DAY = 10;
const DATE_COLUMN_IDX = 2;
const TEXT_COLUMN_IDX = 3;
const MEDIA_COLUMN_IDX = 4;
const POST_TYPE_COLUMN_IDX = 7;
const POST_UPLOAD_TYPE_COLUMN_IDX = 1;
const PIN_BOARD_COLUMN_IDX = 6;
const POST_TITLE_COLUMN_IDX = 5;
const GOOGLE_CTA_COLUMN_IDX = 8;
const GOOGLE_CTA_ACTION_COLUMN_IDX = 9;
const FIRST_COMMENT_CONTENT_COLUMN_IDX = 10;

// Helpers
function readWorkbook(file: File): Promise<XLSX.WorkBook> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target!.result as ArrayBuffer);
      resolve(XLSX.read(data, { type: 'array' }));
    };
    reader.onerror = () => reject(new Error('file_read_error'));
    reader.readAsArrayBuffer(file);
  });
}

function getHeaderRow(sheet: XLSX.WorkSheet): string[] {
  return XLSX.utils.sheet_to_json(sheet, { header: 1 })[0] as string[];
}

type ExcelRow = Record<string, any>;

function validateColumns(
  header: string[]
): { error: ImportError; platform: null; rowIndex: null }[] {
  const errors = [];
  if (header.length !== NUMBER_OF_COLUMNS) {
    errors.push({
      error: ImportError.INVALID_COLUMNS,
      platform: null,
      rowIndex: null
    });
  }
  return errors;
}

function validateEntriesCount(
  rows: ExcelRow[]
): { error: ImportError; platform: null; rowIndex: null }[] {
  const errors = [];
  if (rows.length > MAX_ENTRIES) {
    errors.push({
      error: ImportError.TOO_MANY_ENTRIES,
      platform: null,
      rowIndex: null
    });
  }
  return errors;
}

function validateDates(
  rows: ExcelRow[],
  header: string[]
): { error: ImportError; platform: null; rowIndex: number }[] {
  const errors = [];
  const counts: Record<string, number> = {};
  const key = header[DATE_COLUMN_IDX];
  const now = Date.now();
  const twoHours = now + 2 * 60 * 60 * 1000;

  rows.forEach((r, index) => {
    const dateVal = r[key];
    if (!dateVal) {
      errors.push({
        error: ImportError.MISSING_DATE,
        platform: null,
        rowIndex: index
      });
      return;
    }
    // check if dateVal is a valid date
    if (isNaN(new Date(dateVal).getTime())) {
      errors.push({
        error: ImportError.INVALID_DATE_FORMAT,
        platform: null,
        rowIndex: index
      });
      return;
    }
    const ts = new Date(dateVal).getTime();
    console.log('ts', ts, 'now', now, 'twoHours', twoHours, 'dateVal', dateVal);
    if (ts < twoHours) {
      errors.push({
        error: ImportError.DATE_NOT_FUTURE,
        platform: null,
        rowIndex: index
      });
    }
    const iso = new Date(ts).toISOString().split('T')[0];
    counts[iso] = (counts[iso] || 0) + 1;
    if (counts[iso] > MAX_PER_DAY) {
      errors.push({
        error: ImportError.TOO_MANY_PER_DAY,
        platform: null,
        rowIndex: index
      });
    }
  });

  return errors;
}

function validatePlatforms(
  platforms: any,
  rows: ExcelRow[],
  header: string[]
): { error: ImportError; platform: string | null; rowIndex: number }[] {
  const errors = [];
  const key = header[0];
  rows.forEach((r, index) => {
    const list = String(r[key] || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (!list.length) {
      errors.push({
        error: ImportError.NO_PLATFORMS,
        platform: null,
        rowIndex: index
      });
      return;
    }
    list.forEach((p) => {
      if (!platforms.includes(p)) {
        errors.push({
          error: ImportError.INVALID_PLATFORM,
          platform: p,
          rowIndex: index
        });
      }
    });
  });
  return errors;
}

function validateContent(
  rows: ExcelRow[],
  header: string[]
): { error: ImportError; platform: null; rowIndex: number }[] {
  const errors = [];
  const textKey = header[TEXT_COLUMN_IDX];
  const mediaKey = header[MEDIA_COLUMN_IDX];
  rows.forEach((r, index) => {
    if (!r[textKey] && !r[mediaKey]) {
      errors.push({
        error: ImportError.NO_TEXT_OR_MEDIA,
        platform: null,
        rowIndex: index
      });
    }
  });
  return errors;
}

function validateMediaCounts(
  rows: ExcelRow[],
  header: string[]
): { error: ImportError; platform: string | null; rowIndex: number }[] {
  const errors = [];
  const platformKey = header[0];
  rows.forEach((r, index) => {
    const platforms = String(r[platformKey] || '')
      .split(',')
      .map((s) => s.trim());
    const mediaList = String(r[header[MEDIA_COLUMN_IDX]] || '')
      .split(',')
      .filter(Boolean);

    platforms.forEach((p) => {
      const feats = platformFeatures[p]?.[POST_TYPE_KEY] ?? [];
      const typeName = r[header[POST_TYPE_COLUMN_IDX]] || POST_TYPE_POST;
      const typeKey = POST_TYPE_NAME_TO_TYPE_KEY[typeName];
      const config = feats.find((f) => f.typeKey === typeKey);
      const allowMulti = feats.allowMixedMediaMultiFiles;
      let maxFiles = 0;

      config?.mediaTypes &&
        Object.values(config.mediaTypes).forEach((m) => {
          //@ts-ignore
          if (m.enabled) {
            //@ts-ignore
            maxFiles = allowMulti
              ? //@ts-ignore
                maxFiles + m.maxFiles
              : //@ts-ignore
                Math.max(maxFiles, m.maxFiles);
          }
        });

      const count = mediaList.length;
      if (count > maxFiles) {
        //TODO remove comment!!
        //errors.push({error: ImportError.MEDIA_COUNT_TOO_LARGE, platform: p, rowIndex: index});
      }
      if (count < feats.minFiles) {
        //TODO remove comment!!
        errors.push({
          error: ImportError.MEDIA_COUNT_TOO_FEW,
          platform: p,
          rowIndex: index
        });
      }
    });
  });
  return errors;
}

const validateMediaFilesUrls = (
  rows: ExcelRow[],
  header: string[]
): { error: ImportError; platform: null; rowIndex: number }[] => {
  const errors = [];
  const mediaKey = header[MEDIA_COLUMN_IDX];
  rows.forEach((r, index) => {
    const mediaList = String(r[mediaKey] || '')
      .split(',')
      .filter(Boolean);
    mediaList.forEach((media) => {
      const url = media.trim();
      if (!textIsUrl(url)) {
        errors.push({
          error: ImportError.INVALID_MEDIA_URL,
          platform: null,
          rowIndex: index
        });
      }
    });
  });
  return errors;
};

const validatePostUploadType = (
  rows: ExcelRow[],
  header: string[],
  isMultipostCreator: boolean
): { error: ImportError; platform: null; rowIndex: number }[] => {
  const errors = [];
  const postUploadTypeKey = header[POST_UPLOAD_TYPE_COLUMN_IDX];
  rows.forEach((r, index) => {
    const postUploadType = String(r[postUploadTypeKey] || '').trim();
    if (
      !postUploadType ||
      !Object.keys(LABEL_TO_ACTIONS_MAP).includes(postUploadType)
    ) {
      errors.push({
        error: ImportError.INVALID_POST_CREATE_ACTION,
        platform: null,
        rowIndex: index
      });
    }
    if (isMultipostCreator && postUploadType !== DRAFT) {
      errors.push({
        error: ImportError.INVALID_POST_CREATE_ACTION_MULTIPOST_CREATOR,
        platform: null,
        rowIndex: index
      });
    }
  });
  return errors;
};

const validateGoogleCTA = (
  rows: ExcelRow[],
  header: string[]
): { error: ImportError; platform: null; rowIndex: number }[] => {
  const errors = [];
  const googleCtaKey = header[GOOGLE_CTA_COLUMN_IDX];
  rows.forEach((r, index) => {
    const googleCta = String(r[googleCtaKey] || '')
      .trim()
      .toLowerCase();
    if (!googleCta) {
      return;
    }
    // Check if the googleCta is in the list of valid choices
    if (!GOOGLE_CTA_TYPE_CHOICES.includes(googleCta)) {
      errors.push({
        error: ImportError.INVALID_GOOGLE_CTA,
        platform: null,
        rowIndex: index
      });
    }
  });
  return errors;
};

const validatePostType = (
  rows: ExcelRow[],
  header: string[]
): { error: ImportError; platform: null; rowIndex: number }[] => {
  const errors = [];
  const postTypeKey = header[POST_TYPE_COLUMN_IDX];
  rows.forEach((r, index) => {
    const postType = String(r[postTypeKey] || '').trim() || POST_TYPE_POST;
    if (
      !postType ||
      !Object.keys(POST_TYPE_NAME_TO_TYPE_KEY).includes(postType)
    ) {
      errors.push({
        error: ImportError.INVALID_POST_TYPE,
        platform: null,
        rowIndex: index
      });
    }
  });
  return errors;
};
const getMediaInfo = async (media: string[]) => {
  const mediaInfo = [];
  // send a request to media_metadata
  for (const m of media) {
    // encode the url
    const encodedUrl = encodeURIComponent(m);
    const fileName = m.split('/').pop();
    const url = resolve('media_metadata', {}, {});
    const response = await axiosClient.post(url, {
      url: encodedUrl
    });
    const signedUrl = response.data.signed_url;
    // get info from signed url
    const signedUrlResponse = await fetch(signedUrl);
    const signedUrlData = await signedUrlResponse.json();
    mediaInfo.push({ ...signedUrlData, name: fileName });
  }
  return mediaInfo;
};
const uploadExternalMediaFiles = async (
  mediaInfo: any[],
  mediaUrls: string[],
  location_uuid: string,
  cloud_uuid: string,
  addPresets?: boolean
) => {
  const mediaApiData = [];
  for (let i = 0; i < mediaInfo.length; i++) {
    const media = mediaInfo[i];
    const mediaUrl = encodeURIComponent(mediaUrls[i]);

    console.log('mediaUrl', mediaUrl, 'media', media);
    const uploadPreset = await generateFirebaseUploadUrl(
      {
        content_type: media.contentType,
        filesize_in_bytes: media.size,
        filename: media.name,
        location_uuid: location_uuid,
        cloud_uuid: cloud_uuid,
        width: media?.width,
        height: media?.height,
        upload_type: UPLOAD_TYPE_POST_MEDIA
      },
      {},
      () => {
        console.error('Error generating upload URL');
      }
    );
    const url = resolve('media_download_url', {}, {});
    // send the preset data and media url to this endpoint
    const uploadResponse = await axiosClient.post(url, {
      url: mediaUrls[i],
      uploadPreset: uploadPreset
    });

    // download media file download to firebase
    const downloadUrl = uploadResponse.data.endpoint;
    await axios.post(downloadUrl, uploadResponse.data);
    console.log('Upload response:', uploadResponse.data);
    // Confirm the upload
    const confirmResponse = await axiosClient.put(uploadPreset?.confirm_url, {
      success: true
    });

    if (confirmResponse.data) {
      addPresets
        ? mediaApiData.push({
            ...uploadPreset?.uploaded_object_data,
            uploadPreset: uploadPreset
          })
        : mediaApiData.push(uploadPreset?.uploaded_object_data);
    }
  }
  return mediaApiData;
};
const validateMediaFilesTypes = (
  typeKey: any,
  media: any[],
  platforms: string[],
  rowIndex: number
) => {
  const errors = [];
  platforms.forEach((platform) => {
    const feats = platformFeatures[platform]?.[POST_TYPE_KEY] ?? [];
    console.log('typeKey', typeKey);
    const config = feats.find((f) => f.typeKey === typeKey);
    const allowMulti = feats.allowMixedMediaMultiFiles;

    // TODO check if media type is supported
    const mediaTypes = Object.keys(config.mediaTypes);
    const filesMediaTypes = media.map((m) => m.type);
    // check if all media types are supported
    const allMediaTypesSupported = filesMediaTypes.every((m) =>
      mediaTypes.includes(m)
    );
    if (!allMediaTypesSupported) {
      errors.push({
        error: ImportError.MEDIA_TYPE_NOT_SUPPORTED,
        platform: platform,
        rowIndex: rowIndex
      });
    }
    const mediaHasMultipleTypes =
      media.map((m) => m.type).filter((m, i, a) => a.indexOf(m) === i).length >
      1;
    if (mediaHasMultipleTypes && !allowMulti) {
      errors.push({
        error: ImportError.MULTITYPE_MEDIA_NOT_SUPPORTED,
        platform: platform,
        rowIndex: rowIndex
      });
    }
    const mediaTypesCount = {};
    mediaTypes.forEach((m) => {
      mediaTypesCount[m] = 0;
    });
    media.forEach((m) => {
      const mediaType = m.type;
      if (mediaTypesCount[mediaType] !== undefined) {
        mediaTypesCount[mediaType]++;
      }
    });
    mediaTypes.forEach((m) => {
      const mediaType = m;
      const maxFiles = config.mediaTypes[mediaType].maxFiles;
      if (
        mediaTypesCount[mediaType] > maxFiles &&
        config.mediaTypes[mediaType].enabled
      ) {
        errors.push({
          error: ImportError.MEDIA_COUNT_INVALID,
          platform: platform,
          rowIndex: rowIndex
        });
      }
    });
    // @ts-ignore
    const sumOfMediaTypes = Object.values(mediaTypesCount).reduce(
      // @ts-ignore
      (a, b) => a + b,
      0
    );
    if (sumOfMediaTypes < config.minFiles) {
      errors.push({
        error: ImportError.MEDIA_COUNT_TOO_FEW,
        platform: platform,
        rowIndex: rowIndex
      });
    }

    media.forEach((m) => {
      const mediaType = m.type;
      if (!mediaTypes.includes(mediaType)) {
        return;
      }
      //  checked allowed size
      const maxSize = config.mediaTypes[mediaType]?.maxFileSizeInBytes;
      if (!maxSize) {
        return;
      }
      if (m.size > maxSize) {
        errors.push({
          error: ImportError.MEDIA_SIZE_TOO_LARGE,
          platform: platform,
          rowIndex: rowIndex
        });
      }
    });

    // valiate aspect ratio
    media.forEach((m) => {
      // disabled for now
      return;
      const mediaType = m.type;
      console.log('Processing media:', m);
      if (!mediaTypes.includes(mediaType)) {
        console.log('Media type not supported:', mediaType);
        return;
      }
      if (mediaType !== VIDEO) {
        console.log('Media type is not a video:', mediaType);
        return;
      }
      const aspectRatio = m.width / m.height;
      console.log('Aspect ratio:', aspectRatio);
      console.log('config', config);
      const minAspectRatio =
        config.mediaTypes[mediaType].allowedAspectRatios?.[
          VIDEO_MIN_ASPECT_RATIO
        ];
      const maxAspectRatio =
        config.mediaTypes[mediaType].allowedAspectRatios?.[
          VIDEO_MAX_ASPECT_RATIO
        ];
      console.log(
        'Min aspect ratio:',
        minAspectRatio,
        'Max aspect ratio:',
        maxAspectRatio
      );
      if (minAspectRatio && aspectRatio < minAspectRatio) {
        console.log('Aspect ratio too small:', aspectRatio);
        errors.push({
          error: ImportError.MEDIA_ASPECT_RATIO_TOO_SMALL,
          platform: platform,
          rowIndex: rowIndex
        });
      }
      if (maxAspectRatio && aspectRatio > maxAspectRatio) {
        console.log('Aspect ratio too large:', aspectRatio);
        errors.push({
          error: ImportError.MEDIA_ASPECT_RATIO_TOO_LARGE,
          platform: platform,
          rowIndex: rowIndex
        });
      }
    });
  });
  return errors;
};
const preparePostDataAndUploadMedia = async (
  row: any,
  header: any,
  lomavisCreator: any,
  locations: any,
  rowIndex: number
) => {
  const postTypeSelected = row[header[POST_TYPE_COLUMN_IDX]];
  const locationUUID = lomavisCreator.locationUuid;
  const cloudUUID = lomavisCreator.cloudUuid;
  const postType =
    POST_TYPE_NAME_TO_TYPE_KEY[postTypeSelected] ||
    POST_TYPE_NAME_TO_TYPE_KEY[POST_TYPE_POST];
  const postUploadType = row[header[POST_UPLOAD_TYPE_COLUMN_IDX]];
  const platforms = row[header[0]].split(',').map((p: string) => p.trim());
  const media =
    row[header[MEDIA_COLUMN_IDX]]?.split(',')?.map((m: string) => m.trim()) ||
    [];
  let uploadedFilesData = [];
  try {
    const mediaInfo = await getMediaInfo(media);
    // here do additional checks current knowing file types
    const errors = validateMediaFilesTypes(
      postType,
      mediaInfo,
      platforms,
      rowIndex
    );
    if (errors.length > 0) {
      //throw validationError with the error with type validationError and content as errors array
      throw {
        type: 'validationError',
        content: errors
      };
    }
    uploadedFilesData = await uploadExternalMediaFiles(
      mediaInfo,
      media,
      locationUUID,
      cloudUUID
    );
  } catch (e) {
    console.log('error', e);
    // don't catch the error if it is a validation error
    if (e.type == 'validationError') {
      throw e;
    }
    throw {
      type: 'validationError',
      content: [
        {
          error: ImportError.MEDIA_UPLOAD_FAILED,
          platform: null,
          rowIndex: rowIndex
        }
      ]
    };
  }
  // prepare creator_multipost_config
  const creator_multipost_config = {};
  for (const platform of platforms) {
    const key = `${platform}_${locationUUID}`;
    creator_multipost_config[key] = true;
  }

  // get an export from lomavisCreator
  const action = LABEL_TO_ACTIONS_MAP[postUploadType];
  const post = transferDataToApiFormat(lomavisCreator, action, locations);

  const text = row[header[TEXT_COLUMN_IDX]] || '';
  const lines = text.split('\\n');

  const delta = {
    ops: lines.map((line) => ({ insert: line + '\n' }))
  };

  const quillDeltaJSON = JSON.stringify(delta);

  const postData = {
    // constant values
    ...post,
    creator_multipost_config: JSON.stringify(creator_multipost_config),
    enabled_platforms: platforms.reduce((acc: any, platform: string) => {
      acc[platform] = true;
      return acc;
    }, {}),
    all_platforms_text: quillDeltaJSON,
    post_type: 2,
    action: action,
    media: uploadedFilesData,
    linkedin_document_title: row[header[POST_TITLE_COLUMN_IDX]] || undefined,
    youtube_title: row[header[POST_TITLE_COLUMN_IDX]] || undefined,
    google_cta_url: row[header[GOOGLE_CTA_ACTION_COLUMN_IDX]] || undefined
  };
  // remove platforms platform_post keys
  for (const platform of PLATFORM_LIST_WITH_ALL) {
    const key = `${platform}_post`;
    delete postData[key];
  }

  // now add the platform_post keys

  const platformData = {
    text: {},
    crop_list: [],
    first_comment: row[header[FIRST_COMMENT_CONTENT_COLUMN_IDX]]
      ? JSON.stringify({
          ops: [
            { insert: row[header[FIRST_COMMENT_CONTENT_COLUMN_IDX]] + '\n' }
          ]
        })
      : undefined,
    youtube_tags: undefined, // JSON stringified or undefined
    google_cta_type: row[header[GOOGLE_CTA_COLUMN_IDX]] || null,
    media: uploadedFilesData,
    linkedin_document_title: row[header[POST_TITLE_COLUMN_IDX]] || undefined,
    youtube_title: row[header[POST_TITLE_COLUMN_IDX]] || undefined,
    google_cta_url: row[header[GOOGLE_CTA_ACTION_COLUMN_IDX]] || undefined
    //[type_prefix + "_tags"]: string, // e.g., "general_tags": comma-separated string
    //instagram_user_tags?: string // JSON stringified array of arrays (tags per media item)
  };

  for (const platform of [ALL, ...platforms]) {
    const platformKey = `${platform}_post`;
    const platformPostData = {
      ...platformData,
      text: quillDeltaJSON,
      // use this format: YYYY-MM-DDThh:mm[:ss[.uuuuuu]][+HH:MM|-HH:MM|Z]."

      planned_publication_datetime: new Date(
        row[header[DATE_COLUMN_IDX]]
      ).toISOString()
    };
    postData[platformKey] = platformPostData;
  }
  return postData;
};

export {
  readWorkbook,
  getHeaderRow,
  validateColumns,
  validateEntriesCount,
  validateDates,
  validatePlatforms,
  validateContent,
  validateMediaCounts,
  validateMediaFilesUrls,
  validatePostUploadType,
  ImportError,
  preparePostDataAndUploadMedia,
  validateGoogleCTA,
  validatePostType,
  getMediaInfo,
  uploadExternalMediaFiles
};
